(() => {
  const configs = new Map();
  const holidayCache = new Map();
  const key = date => date.toISOString().slice(0, 10);
  const next = (date, days = 1) => new Date(date.getTime() + days * 86400000);

  async function loadConfig(url) {
    if (!url) throw new Error('Missing delivery configuration');
    if (!configs.has(url)) {
      const request = fetch(url).then(response => {
        if (!response.ok) throw new Error('Delivery configuration unavailable');
        return response.json();
      });
      configs.set(url, request);
      request.catch(() => { if (configs.get(url) === request) configs.delete(url); });
    }
    return configs.get(url);
  }

  function frenchHolidays(year) {
    if (holidayCache.has(year)) return holidayCache.get(year);
    // Gregorian Easter (Meeus/Jones/Butcher); metropolitan France's national holidays.
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = (h + l - 7 * m + 114) % 31 + 1;
    const easter = new Date(Date.UTC(year, month - 1, day));
    const dates = new Set(['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25'].map(value => `${year}-${value}`));
    [1, 39, 50].forEach(offset => dates.add(key(next(easter, offset))));
    holidayCache.set(year, dates);
    return dates;
  }

  function validWeek(days) {
    return Array.isArray(days) && days.length > 0 && days.every(day => Number.isInteger(day) && day >= 0 && day <= 6);
  }

  function estimate(config, country, now = new Date()) {
    const range = config?.country?.[country];
    const calendar = config?.calendar;
    if (config?.unit !== 'jours_ouvrables' || config.delivery_time_after_shipping !== true || !range || !calendar) return null;
    const deliveryDays = range.delivery_days ?? calendar.delivery_days;
    if (!Number.isInteger(range.min) || !Number.isInteger(range.max) || range.min < 0 || range.max < range.min || range.max > 366 ||
        !validWeek(calendar.dispatch_days) || !validWeek(deliveryDays) ||
        !Number.isInteger(calendar.cutoff_time) || calendar.cutoff_time < 0 || calendar.cutoff_time > 23) return null;
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: calendar.time_zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(now).map(part => [part.type, part.value]));
    const today = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day));
    const dispatchClosures = new Set(calendar.shipping_blackout_dates || []);
    const destinationHolidays = new Set(range.jours_feries || []);
    const isFrenchHoliday = date => calendar.exclude_french_holidays && frenchHolidays(date.getUTCFullYear()).has(key(date));
    const canDispatch = date => calendar.dispatch_days.includes(date.getUTCDay()) && !isFrenchHoliday(date) && !dispatchClosures.has(key(date));
    const canDeliver = date => deliveryDays.includes(date.getUTCDay()) && !isFrenchHoliday(date) && !destinationHolidays.has(key(date));
    const currentMinutes = (+parts.hour * 60) + (+parts.minute);
    const cutoffMinutes = calendar.cutoff_time * 60;

    let dispatch = currentMinutes < cutoffMinutes ? today : next(today);  
    let attempts = 0;
    while (!canDispatch(dispatch)) {
      if (++attempts > 730) return null;
      dispatch = next(dispatch);
    }
    function arrival(duration) {
      let date = dispatch, remaining = duration, scanned = 0;
      while (remaining > 0 || !canDeliver(date)) {
        if (++scanned > 3000) return null;
        date = next(date);
        if (remaining > 0 && canDeliver(date)) remaining--;
      }
      return date;
    }
    const min = arrival(range.min), max = arrival(range.max);
    return min && max ? { today, dispatch, min, max } : null;
  }

  function formatDeliveryDate(date, locale = 'fr') {
    return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: 'numeric', month: 'short' })
      .formatToParts(date).map(part => part.type === 'month' && locale.startsWith('fr') ? part.value.replace(/\.$/, '') : part.value).join('');
  }

class DeliveryPromise extends HTMLElement {
  static get observedAttributes() {
    return ['data-country', 'data-locale', 'data-config-url'];
  }

  connectedCallback() {
    this.sequence = 0;
    this.selectedCountry = this.dataset.country;

    this.countrySelect = this.querySelector('[data-delivery-country]');
    const countrySize = this.querySelector('[data-delivery-country-size]');
    // Explicit sizing avoids Safari using the longest option as the select width.
    this.containerWidth = null;
    this.countrySizeObserver = new ResizeObserver(entries => {
      const container = entries.find(entry => entry.target === this.parentElement);
      const countryChanged = entries.some(entry => entry.target === countrySize);
      if (!countryChanged && container?.contentRect.width === this.containerWidth) return;
      if (container) this.containerWidth = container.contentRect.width;
      const width = countrySize.getBoundingClientRect().width;
      if (width > 0) this.countrySelect?.style.setProperty('--delivery-country-width', `${Math.ceil(width)}px`);
      this.fitDeliveryText();
    });
    if (countrySize) this.countrySizeObserver.observe(countrySize);
    if (this.parentElement) this.countrySizeObserver.observe(this.parentElement);
    this.estimateElement = this.querySelector('[data-delivery-estimate]');
    this.fallbackElement = this.querySelector('[data-delivery-fallback]');
    this.countryLabelElement = this.querySelector('[data-country-label]');
    this.beforeCountryElement = this.querySelector('[data-delivery-before-country]');

    this.refresh = () => this.update();

    this.onVisibility = () => {
      if (!document.hidden) this.update();
    };

    this.onCountryChange = () => {
      this.selectedCountry = this.countrySelect.value;
      this.updateEstimate();
    };

    this.countrySelect?.addEventListener('change', this.onCountryChange);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.timer = setInterval(this.refresh, 300000);

    this.update();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;

    if (name === 'data-country') {
      this.selectedCountry = newValue;
      this.hidden = true;
    }

    if (this.refresh) this.update();
  }

  disconnectedCallback() {
    this.sequence++;
    this.countrySizeObserver?.disconnect();

    clearInterval(this.timer);

    this.countrySelect?.removeEventListener('change', this.onCountryChange);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  fitDeliveryText() {
    if (this.hidden || !this.isConnected || !this.closest('.delivery-promise--buy-buttons')) return;
    const text = this.querySelector('.delivery-promise__text');
    if (!text) return;
    // Wrap naturally, then remove trailing unused space from the left-aligned lines.
    text.style.width = '';
    const bounds = text.getBoundingClientRect();
    const rects = Array.from(text.children).filter(child => !child.hidden)
      .flatMap(child => Array.from(child.getClientRects()));
    if (bounds.width && rects.length) {
      const width = Math.max(...rects.map(rect => rect.right)) - bounds.left;
      text.style.width = `${Math.min(bounds.width, Math.ceil(width) + 1)}px`;
    }
  }

  buildCountrySelect(config) {
    if (!this.countrySelect) return;

    const locale = this.dataset.locale || 'fr';
    const countries = Object.keys(config?.country || {});

    let displayNames;

    try {
      displayNames = new Intl.DisplayNames([locale], {
        type: 'region'
      });
    } catch (_) {
      displayNames = new Intl.DisplayNames(['fr'], {
        type: 'region'
      });
    }

    const currentValue = this.selectedCountry;

    this.countrySelect.innerHTML = '';

    countries
      .map(code => ({
        code,
        name: displayNames.of(code) || code
      }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, locale, {
          sensitivity: 'base'
        })
      )
      .forEach(country => {
        const option = document.createElement('option');

        option.value = country.code;
        option.textContent = country.name;

        this.countrySelect.appendChild(option);
      });

    const otherOption = document.createElement('option');
    otherOption.value = '__OTHER__';
    otherOption.textContent =
      this.dataset.otherCountry || 'Autre pays';

    this.countrySelect.appendChild(otherOption);

    if (countries.includes(currentValue)) {
      this.countrySelect.value = currentValue;
      this.selectedCountry = currentValue;
    } else {
      this.countrySelect.value = '__OTHER__';
      this.selectedCountry = '__OTHER__';
    }

    if (this.countryLabelElement) {
      this.countryLabelElement.textContent =
        this.dataset.countryLabel || '';
    }

    if (this.beforeCountryElement) {
      this.beforeCountryElement.textContent = this.dataset.beforeCountry || '';
    }
  }

  updateEstimate() {
    if (!this.config) return;

    // Eligibility follows the storefront localization, not the dropdown choice.
    if (!Object.prototype.hasOwnProperty.call(this.config.country || {}, this.dataset.country)) {
      this.hidden = true;
      return;
    }

    const countrySize = this.querySelector('[data-delivery-country-size]');
    if (countrySize) {
      countrySize.textContent = this.countrySelect?.selectedOptions[0]?.textContent || '';
    }

    if (this.selectedCountry === '__OTHER__') {
      if (this.estimateElement) {
        this.estimateElement.hidden = true;
        this.estimateElement.textContent = '';
      }

      if (this.fallbackElement) {
        this.fallbackElement.textContent =
          this.dataset.checkoutMessage || '';
        this.fallbackElement.hidden = false;
      }

      this.hidden = false;
      this.fitDeliveryText();
      return;
    }

    const dates = estimate(
      this.config,
      this.selectedCountry
    );

    if (!dates) {
      if (this.estimateElement) {
        this.estimateElement.hidden = true;
        this.estimateElement.textContent = '';
      }

      if (this.fallbackElement) {
        this.fallbackElement.textContent =
          this.dataset.checkoutMessage || '';
        this.fallbackElement.hidden = false;
      }

      this.hidden = false;
      this.fitDeliveryText();
      return;
    }

    const locale = this.dataset.locale || 'fr';

    const tomorrow = key(next(dates.today));
    const minIsTomorrow = key(dates.min) === tomorrow;
    const maxIsTomorrow = key(dates.max) === tomorrow;
    const min = minIsTomorrow ? this.dataset.tomorrow : formatDeliveryDate(dates.min, locale);
    const max = maxIsTomorrow ? this.dataset.tomorrow : formatDeliveryDate(dates.max, locale);

    if (this.estimateElement) {
      if (key(dates.min) === key(dates.max)) {
        this.estimateElement.innerHTML =
          (minIsTomorrow ? '{date}' : this.dataset.date).replace('{date}', `<strong>${min}</strong>`);
      } else {
        this.estimateElement.innerHTML =
          (minIsTomorrow ? this.dataset.datesFromTomorrow : this.dataset.dates)
            .replace('{min}', `<span class="font-medium-bold">${min}</span>`)
            .replace('{max}', `<span class="font-medium-bold">${max}</span>`);
      }

      this.estimateElement.hidden = false;
    }

    if (this.fallbackElement) {
      this.fallbackElement.hidden = true;
      this.fallbackElement.textContent = '';
    }

    this.hidden = false;
    this.fitDeliveryText();
  }

  async update() {
    const sequence = ++this.sequence;

    try {
      const config = await loadConfig(
        this.dataset.configUrl
      );

      if (
        !this.isConnected ||
        sequence !== this.sequence
      ) {
        return;
      }

      this.config = config;

      this.buildCountrySelect(config);
      this.updateEstimate();

    } catch (_) {
      if (sequence === this.sequence) {
        this.hidden = true;
      }
    }
  }
}
  if (!customElements.get('delivery-promise')) customElements.define('delivery-promise', DeliveryPromise);
})();
